import { message } from 'antd';
import * as React from 'react';

// 下载任务状态枚举
export enum DownloadTaskStatus {
    PENDING = 1,     // 等待下载
    DOWNLOADING = 2, // 正在下载
    SUCCESS = 3,     // 下载成功
    FAILED = 4,      // 下载失败
    CANCELLED = 5    // 已取消
}

// 下载任务接口
export interface DownloadTask {
    id: string;
    fileKey: string;        // 文件唯一标识（用于防止重复下载）
    fileName: string;
    status: DownloadTaskStatus;
    progress: number;       // 下载进度 0-100
    downloadUrl?: string;
    failReason?: string;
    createTime: number;
    fileSize?: number;      // 文件大小（字节）
    downloadedSize?: number; // 已下载大小（字节）
}

// 下载任务管理器
class DownloadTaskManager {
    private tasks: Map<string, DownloadTask> = new Map();

    private listeners: Set<(tasks: DownloadTask[]) => void> = new Set();

    // 正在下载的文件key集合（用于防止重复下载）
    private downloadingFileKeys: Set<string> = new Set();

    // 存储 AbortController，用于取消下载
    private abortControllers: Map<string, AbortController> = new Map();

    // 添加任务状态监听器
    addListener(listener: (tasks: DownloadTask[]) => void) {
        this.listeners.add(listener);
        // 立即通知当前状态
        listener(this.getAllTasks());
    }

    // 移除监听器
    removeListener(listener: (tasks: DownloadTask[]) => void) {
        this.listeners.delete(listener);
    }

    // 通知所有监听器
    private notifyListeners() {
        const tasks = this.getAllTasks();
        this.listeners.forEach(listener => {
            try {
                listener(tasks);
            } catch (error) {
                console.error('Download task listener error:', error);
            }
        });
    }

    // 检查文件是否正在下载
    isFileDownloading(fileKey: string): boolean {
        return this.downloadingFileKeys.has(fileKey);
    }

    // 添加下载任务
    addTask(fileKey: string, fileName: string, downloadUrl?: string): string | null {
        // 检查是否已经在下载
        if (this.downloadingFileKeys.has(fileKey)) {
            message.warning(`文件 "${fileName}" 正在下载中，请勿重复点击`);
            return null;
        }

        const taskId = `download_${fileKey}_${Date.now()}`;
        const task: DownloadTask = {
            id: taskId,
            fileKey,
            fileName,
            status: DownloadTaskStatus.PENDING,
            progress: 0,
            downloadUrl,
            createTime: Date.now(),
        };

        this.tasks.set(taskId, task);
        this.downloadingFileKeys.add(fileKey);
        this.notifyListeners();

        return taskId;
    }

    // 更新任务状态
    updateTask(taskId: string, updates: Partial<DownloadTask>) {
        const task = this.tasks.get(taskId);
        if (task) {
            Object.assign(task, updates);
            this.tasks.set(taskId, task);
            this.notifyListeners();
        }
    }

    // 更新下载进度
    updateProgress(taskId: string, progress: number, downloadedSize?: number, fileSize?: number) {
        const task = this.tasks.get(taskId);
        if (task) {
            task.progress = Math.min(100, Math.max(0, progress));
            task.status = DownloadTaskStatus.DOWNLOADING;
            if (downloadedSize !== undefined) task.downloadedSize = downloadedSize;
            if (fileSize !== undefined) task.fileSize = fileSize;
            this.tasks.set(taskId, task);
            this.notifyListeners();
        }
    }

    // 标记任务成功
    markSuccess(taskId: string) {
        const task = this.tasks.get(taskId);
        if (task) {
            task.status = DownloadTaskStatus.SUCCESS;
            task.progress = 100;
            this.tasks.set(taskId, task);
            this.downloadingFileKeys.delete(task.fileKey);
            this.notifyListeners();
        }
    }

    // 标记任务失败
    markFailed(taskId: string, reason?: string) {
        const task = this.tasks.get(taskId);
        if (task) {
            task.status = DownloadTaskStatus.FAILED;
            task.failReason = reason || '下载失败';
            this.tasks.set(taskId, task);
            this.downloadingFileKeys.delete(task.fileKey);
            this.notifyListeners();
            message.error(`文件 "${task.fileName}" 下载失败: ${task.failReason}`);
        }
    }

    // 获取所有任务
    getAllTasks(): DownloadTask[] {
        return Array.from(this.tasks.values()).sort((a, b) => b.createTime - a.createTime);
    }

    // 获取进行中的任务数量
    getActiveTasksCount(): number {
        return Array.from(this.tasks.values()).filter(
            task => task.status === DownloadTaskStatus.PENDING || task.status === DownloadTaskStatus.DOWNLOADING
        ).length;
    }

    // 移除任务
    removeTask(taskId: string) {
        const task = this.tasks.get(taskId);
        if (task) {
            this.downloadingFileKeys.delete(task.fileKey);
        }
        this.tasks.delete(taskId);
        this.notifyListeners();
    }

    // 清除所有已完成的任务（成功、失败或已取消）
    clearCompletedTasks() {
        const activeTasks = new Map<string, DownloadTask>();
        this.tasks.forEach((task, id) => {
            if (task.status === DownloadTaskStatus.PENDING || task.status === DownloadTaskStatus.DOWNLOADING) {
                activeTasks.set(id, task);
            } else {
                this.downloadingFileKeys.delete(task.fileKey);
            }
        });
        this.tasks = activeTasks;
        this.notifyListeners();
    }

    // 取消下载任务
    cancelDownload(taskId: string) {
        const task = this.tasks.get(taskId);
        if (!task) return;

        // 只能取消正在等待或下载中的任务
        if (task.status !== DownloadTaskStatus.PENDING && task.status !== DownloadTaskStatus.DOWNLOADING) {
            return;
        }

        // 中止下载请求
        const controller = this.abortControllers.get(taskId);
        if (controller) {
            controller.abort();
            this.abortControllers.delete(taskId);
        }

        // 更新任务状态为已取消
        task.status = DownloadTaskStatus.CANCELLED;
        task.failReason = '下载已取消';
        this.tasks.set(taskId, task);
        this.downloadingFileKeys.delete(task.fileKey);
        this.notifyListeners();

        message.info(`文件 "${task.fileName}" 下载已取消`);

        // 从任务列表中移除（可选：也可以保留让用户看到已取消状态）
        setTimeout(() => {
            this.removeTask(taskId);
        }, 1500);
    }

    // 执行下载（带进度）
    async downloadWithProgress(
        taskId: string,
        url: string,
        fileName: string,
        options: {
            method?: 'GET' | 'POST';
            headers?: Record<string, string>;
            withCredentials?: boolean;
        } = {}
    ): Promise<boolean> {
        const task = this.tasks.get(taskId);
        if (!task) return false;

        const { method = 'GET', headers = {}, withCredentials = false } = options;

        // 创建 AbortController 用于取消下载
        const abortController = new AbortController();
        this.abortControllers.set(taskId, abortController);

        try {
            // 更新状态为下载中
            this.updateTask(taskId, { status: DownloadTaskStatus.DOWNLOADING });

            // console.log('url================', url)
            // if (url) {
            //     // 使用 React 元素创建可点击的提示
            //     const content = React.createElement(
            //         'div',
            //         null,
            //         `文件 "${fileName}" 正在后台下载中...`,
            //         React.createElement(
            //             'a',
            //             {
            //                 href: url,
            //                 target: '_blank',
            //                 rel: ['noopener', 'noreferrer'], // 改为数组形式
            //                 download: fileName,
            //                 style: {
            //                     color: '#1890ff',
            //                     textDecoration: 'underline',
            //                     marginLeft: '8px',
            //                 },
            //             },
            //             `点击此处也可手动下载 "${fileName}"`
            //         )
            //     );

            //     // 使用 message 的自定义内容功能
            //     message.info({
            //         content: content,
            //         duration: 10,
            //     });
            // }

            const response = await fetch(url, {
                method,
                headers: {
                    ...headers
                },
                credentials: withCredentials ? 'include' : 'same-origin',
                signal: abortController.signal, // 添加取消信号
            });

            if (!response.ok) {
                throw new Error(`下载失败，状态码：${response.status}`);
            }

            // 获取文件大小
            const contentLength = response.headers.get('content-length');
            const totalSize = contentLength ? parseInt(contentLength, 10) : 0;

            // 从 Content-Disposition 中获取文件名
            const disposition = response.headers.get('content-disposition');
            let downloadFileName = fileName;
            if (disposition) {
                const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
                if (match) {
                    downloadFileName = decodeURIComponent(match[1]);
                }
            }

            // 更新文件大小
            if (totalSize > 0) {
                this.updateTask(taskId, { fileSize: totalSize });
            }

            // 使用 ReadableStream 读取数据并追踪进度
            const reader = response.body?.getReader();
            if (!reader) {
                throw new Error('无法读取响应流');
            }

            const chunks: BlobPart[] = [];
            let downloadedSize = 0;

            while (true) {
                // 检查是否已取消
                if (abortController.signal.aborted) {
                    reader.cancel();
                    throw new Error('下载已取消');
                }

                const { done, value } = await reader.read();
                if (done) break;

                chunks.push(value);
                downloadedSize += value.length;

                // 计算进度
                if (totalSize > 0) {
                    const progress = Math.round((downloadedSize / totalSize) * 100);
                    this.updateProgress(taskId, progress, downloadedSize, totalSize);
                } else {
                    // 如果没有 content-length，显示已下载大小
                    this.updateTask(taskId, { downloadedSize });
                }
            }

            // 合并所有块
            const blob = new Blob(chunks);
            const urlBlob = window.URL.createObjectURL(blob);

            // 创建下载链接
            const a = document.createElement('a');
            a.href = urlBlob;
            a.download = downloadFileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(urlBlob);

            // 清理 AbortController
            this.abortControllers.delete(taskId);

            // 标记成功
            this.markSuccess(taskId);
            message.success(`文件 "${downloadFileName}" 下载成功`);
            return true;

        } catch (error) {
            // 清理 AbortController
            this.abortControllers.delete(taskId);

            // 如果是取消操作，不显示错误
            if (error instanceof Error && (error.name === 'AbortError' || error.message === '下载已取消')) {
                // 已在 cancelDownload 中处理
                return false;
            }

            console.error('下载文件失败:', error);
            this.markFailed(taskId, error instanceof Error ? error.message : '下载失败');
            return false;
        }
    }

    // 销毁管理器
    destroy() {
        // 取消所有进行中的下载
        this.abortControllers.forEach(controller => {
            controller.abort();
        });
        this.abortControllers.clear();
        this.tasks.clear();
        this.downloadingFileKeys.clear();
        this.listeners.clear();
    }
}

// 创建全局单例
export const downloadTaskManager = new DownloadTaskManager();

// 页面卸载时清理
if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
        downloadTaskManager.destroy();
    });
}
